import { CreateTagPill, TagPill, TextTooltip } from '@components'
import TagSelectorDropDown from '@root/containers/TagSelector/TagSelectorDropDown'
import { useTagSelectorFloating } from '@root/containers/TagSelector/useTagSelectorFloating'
import { IUsershipTagHelp } from '@root/query/constants'
import { useUsershipTags } from '@root/query/hooks'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaEnvelope, FaLockOpen, FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'
import { getBusinessRoleIcon, roleById } from '@root/query/usershipTags'

// Fits the search field, one role name, and the checkmark without covering the permission presets.
const ROLE_DROPDOWN_MIN_WIDTH = 280

interface Props {
  value: string[]
  onChange: (tags: string[]) => void
}

export const BusinessRoleSelector: React.FC<Props> = ({ value, onChange }) => {
  const [t] = useTranslation()
  const selected = value ?? []
  const { data: catalog } = useUsershipTags()
  const roles = catalog?.roles ?? []
  const { isDropDownOpen, toggleDropDown, context, refs, getReferenceProps, getFloatingProps } =
    useTagSelectorFloating({ minWidth: ROLE_DROPDOWN_MIN_WIDTH })

  const toggleRole = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(tag => tag !== id) : [...selected, id])
  }

  return (
    <>
      <StyledLabel>
        {t('settings.people.businessRoles.label')}
        {catalog?.help && (
          <TextTooltip tooltip={<BusinessRolesInfo help={catalog.help} />}>
            <FaQuestionCircle />
          </TextTooltip>
        )}
      </StyledLabel>

      <StyledContainer {...getReferenceProps()} ref={refs.setReference}>
        <TagRow>
          {selected.map(id => {
            const role = roleById(roles, id)
            return (
              <TagPill
                key={id}
                tag={{ id, name: role.name, description: role.description }}
                icon={getBusinessRoleIcon(id)}
                onDisMiss={tagId => onChange(selected.filter(tag => tag !== tagId))}
                maxWidth={'100%'}
              />
            )
          })}
          <CreateTagPill
            onClick={toggleDropDown}
            text={selected.length ? undefined : t('settings.people.businessRoles.add')}
          />
        </TagRow>
        {isDropDownOpen && (
          <TagSelectorDropDown
            context={context}
            refs={refs}
            getFloatingProps={getFloatingProps}
            items={roles.map(role => ({
              id: role.id,
              name: role.name,
              description: role.description,
              icon: getBusinessRoleIcon(role.id)
            }))}
            searchPlaceholder={t('settings.people.businessRoles.searchPlaceholder')}
            onTagSelect={toggleRole}
            isSelected={role => selected.includes(role.id)}
          />
        )}
      </StyledContainer>
    </>
  )
}

const BusinessRolesInfo: React.FC<{ help: IUsershipTagHelp }> = ({ help }) => {
  const [before, after] = help.access.split('{permissions}')

  return (
    <InfoBody>
      <strong>{help.title}</strong>
      <p>{help.what}</p>
      <InfoLine>
        <FaLockOpen />
        <span>
          {after === undefined ? (
            help.access
          ) : (
            <>
              {before}
              <b>{help.permissions}</b>
              {after}
            </>
          )}
        </span>
      </InfoLine>
      <InfoLine>
        <FaEnvelope />
        <span>{help.workflows}</span>
      </InfoLine>
    </InfoBody>
  )
}

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  white-space: nowrap;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: ${({ theme }) => theme.fontSize.md}rem;
  margin: ${({ theme }) => theme.spacing.md}rem 0 ${({ theme }) => theme.spacing.xs}rem;

  svg {
    display: block;
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    cursor: pointer;
  }
`

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const InfoBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  p {
    margin: 0;
  }
`

const InfoLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  svg {
    flex-shrink: 0;
    width: ${({ theme }) => theme.fontSize.px11}rem;
    height: ${({ theme }) => theme.fontSize.px11}rem;
    margin-top: 0.15rem;
    opacity: 0.8;
  }
`
